import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AssistantProvider } from "./contexts/AssistantContext";
import "./lib/i18n";

import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ConfirmSignup from "./pages/ConfirmSignup";
import Profile from "./pages/Profile";
import Dashboard from "./pages/Dashboard";
import QuotationsList from "./pages/QuotationsList";
import QuotationNew from "./pages/QuotationNew";
import QuotationDetail from "./pages/QuotationDetail";
import CustomersList from "./pages/CustomersList";
import Settings from "./pages/Settings";
import NotificationsList from "./pages/NotificationsList";
import PublicQuotation from "./pages/PublicQuotation";
import AcceptOwnerInvite from "./pages/AcceptOwnerInvite";
import AcceptMemberInvite from "./pages/AcceptMemberInvite";
import OrganizationUsers from "./pages/OrganizationUsers";
import OrganizationBlocked from "./pages/OrganizationBlocked";
import PlatformAdminOrganizations from "./pages/PlatformAdminOrganizations";
import PlatformAdminOrganizationDetail from "./pages/PlatformAdminOrganizationDetail";
import PlatformAccessGate from "./components/platform/PlatformAccessGate";
import { isOrgActive } from "./lib/orgLifecycle";
import { useAuthenticatedLanding } from "./hooks/useAuthenticatedLanding";

const Waiting = () => (
  <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#f6f8fb", color: "#64748b" }}>
    Зареждане…
  </div>
);

function Guarded({ children }) {
  const { loading, session, orgs, orgsLoaded, orgId, currentOrg } = useAuth();
  // Phase 2D.1B fix — a zero-organization user is not automatically an
  // unfinished tenant signup: a standalone platform super administrator has
  // no organization by design. The landing resolver makes that call from the
  // same backend-authoritative platform-access check PlatformAccessGate uses.
  const { loading: landingLoading, path: landingPath } = useAuthenticatedLanding();
  if (loading || (session && !orgsLoaded)) {
    return <Waiting />;
  }
  if (!session) return <Navigate to="/login" replace />;
  // Phase 2D.2B — an authenticated user without an organization has an UNFINISHED
  // signup, not a fresh one — UNLESS they are a verified platform admin, who never
  // has one. Sending a genuine unfinished signup to /register was the confirmation
  // loop: it asked for the account and organization data all over again. They now
  // go to the onboarding completion route, which finishes the existing bootstrap.
  if (orgs?.length === 0) {
    if (landingLoading) return <Waiting />;
    return <Navigate to={landingPath} replace />;
  }
  if (!orgId) return <Navigate to="/auth/confirm-signup" replace />;
  // Phase 2D.1C — a non-active organization never reaches a tenant module. The
  // backend refuses those requests anyway; this replaces the resulting wall of
  // errors with one Bulgarian explanation and blocks the navigation entirely.
  if (currentOrg && !isOrgActive(currentOrg)) return <OrganizationBlocked />;
  return children;
}

// Phase 2D.1B fix — resolves "/" and any unmatched path for an authenticated
// user instead of assuming /dashboard, which sent a standalone platform admin
// straight into a Guarded redirect to tenant onboarding. See lib/landing.js.
function LandingRedirect() {
  const { loading, path } = useAuthenticatedLanding();
  if (loading) return <Waiting />;
  return <Navigate to={path} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/* Phase 4A.5A — the Assistant session owner sits ABOVE <Routes>: each
            page renders its own <Layout>, so a conversation held inside one
            would be destroyed by ordinary navigation, and an in-flight turn
            would lose the identity it must be checked against. */}
        <AssistantProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            {/* Phase 2D.2A — password recovery. Both routes are public by design:
                the reset screen is gated by a genuine Supabase recovery session,
                never by an ordinary authenticated session. */}
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            {/* Phase 2D.2B — signup e-mail confirmation callback. PUBLIC and
                deliberately outside <Guarded>: it must be able to finish the
                organization bootstrap before any "user has no organization" guard
                can interfere. It is unlocked by a genuine Supabase session only. */}
            <Route path="/auth/confirm-signup" element={<ConfirmSignup />} />
            {/* Phase 2D.1C — Owner invitation acceptance. PUBLIC and outside
                <Guarded>: the invited person belongs to no organization yet, which
                is exactly what acceptance fixes. The one-time token arrives in the
                URL FRAGMENT, is stripped before this route renders and is never
                sufficient on its own — a verified Supabase session whose e-mail
                matches the invitation is mandatory, and the database decides the
                organization, the role and the seat. */}
            <Route path="/auth/accept-owner-invite" element={<AcceptOwnerInvite />} />
            {/* Phase 2D.2 checkpoint 2B — tenant (Мениджър / Търговец) invitation
                acceptance. PUBLIC and outside <Guarded> for the same reason as the
                Owner route: the invited person belongs to no organization yet.
                /auth/accept-invite is the CANONICAL path — it is the one the backend
                puts in an e-mail and gives Supabase as redirect_to — and
                /auth/accept-invitation is a compatibility alias that renders the
                SAME component, so there is only ever one acceptance implementation.
                Both are client-side routes, never a server redirect, because a
                redirect would drop the URL fragment that carries the token. */}
            <Route path="/auth/accept-invite" element={<AcceptMemberInvite />} />
            <Route path="/auth/accept-invitation" element={<AcceptMemberInvite />} />
            <Route path="/q/:token" element={<PublicQuotation />} />
            <Route path="/dashboard" element={<Guarded><Dashboard /></Guarded>} />
            <Route path="/quotations" element={<Guarded><QuotationsList /></Guarded>} />
            <Route path="/quotations/new" element={<Guarded><QuotationNew /></Guarded>} />
            <Route path="/quotations/:id" element={<Guarded><QuotationDetail /></Guarded>} />
            <Route path="/customers" element={<Guarded><CustomersList /></Guarded>} />
            <Route path="/profile" element={<Guarded><Profile /></Guarded>} />
            <Route path="/settings" element={<Guarded><Settings /></Guarded>} />
            {/* Phase 2D.2 checkpoint 2B — tenant user management. Guarded like every
                other tenant module; the page itself shows the Bulgarian forbidden
                card to a Sales user, and the backend refuses those endpoints for
                them regardless, so a direct navigation leaks nothing. */}
            <Route path="/organization/users" element={<Guarded><OrganizationUsers /></Guarded>} />
            <Route path="/notifications" element={<Guarded><NotificationsList /></Guarded>} />
            {/* Phase 2D.1B — platform administration. Guarded by PlatformAccessGate,
                NOT by Guarded: it requires no customer organization and no
                organization role, only an active platform super administrator. */}
            <Route
              path="/platform-admin"
              element={<PlatformAccessGate><PlatformAdminOrganizations /></PlatformAccessGate>}
            />
            <Route
              path="/platform-admin/organizations/:organizationId"
              element={<PlatformAccessGate><PlatformAdminOrganizationDetail /></PlatformAccessGate>}
            />
            <Route path="/" element={<LandingRedirect />} />
            <Route path="*" element={<LandingRedirect />} />
          </Routes>
        </AssistantProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
